import { account } from './account.js';

/* Prueba de velocidad de un stream de vídeo bajo demanda. Mide dos tramos:
   - proveedor → servidor Filoteca (lo mide el servidor en /speedtest), solo cuando el stream
     pasa por el proxy;
   - servidor (o proveedor) → este dispositivo, descargando el inicio del fichero unos segundos.
   Con el tamaño del fichero y su duración se estima el caudal que necesita la película. */

const TEST_MS = 6000;
const MAX_BYTES = 40 * 1024 * 1024;

function sizeFromHeaders(res) {
    const cr = /\/(\d+)\s*$/.exec(res.headers.get('content-range') || '');
    return cr ? Number(cr[1]) : Number(res.headers.get('content-length')) || 0;
}

/** Descarga `url` durante TEST_MS (o hasta MAX_BYTES) y devuelve el caudal en kb/s. */
export async function measureDownload(url, { ms = TEST_MS, maxBytes = MAX_BYTES } = {}) {
    const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
    const guard = setTimeout(() => controller && controller.abort(), ms + 10000);
    const started = performance.now();
    let res;
    try {
        res = await fetch(url, { headers: { Range: 'bytes=0-' }, cache: 'no-store', signal: controller ? controller.signal : undefined });
    } catch (e) {
        clearTimeout(guard);
        throw new Error('network');
    }
    if (!res.ok) {
        clearTimeout(guard);
        if (controller) controller.abort();
        throw new Error('HTTP ' + res.status);
    }
    const size = sizeFromHeaders(res);
    const reader = res.body.getReader();
    const t0 = performance.now();
    let bytes = 0;
    try {
        for (;;) {
            const { done, value } = await reader.read();
            if (done) break;
            bytes += value.byteLength;
            if (bytes >= maxBytes || performance.now() - t0 >= ms) break;
        }
    } finally {
        clearTimeout(guard);
        try { reader.cancel(); } catch (e) { /* ya cerrado */ }
        if (controller) controller.abort();
    }
    const elapsed = performance.now() - t0;
    return { bytes, ms: Math.round(elapsed), kbps: elapsed ? Math.round((bytes * 8) / elapsed) : 0, size, ttfb: Math.round(t0 - started) };
}

/**
 * Ejecuta la prueba completa para un vídeo.
 * @param {object} o
 * @param {string} o.rawUrl   URL del proveedor
 * @param {string} o.playUrl  URL que usa el reproductor (con proxy si aplica)
 * @param {number} [o.durationSecs]
 * @param {number} [o.bitrateKbps]  bitrate declarado por el panel, si lo hay
 * @param {(step: 'server'|'device') => void} [o.onStep]
 */
export async function runSpeedTest({ rawUrl, playUrl, durationSecs = 0, bitrateKbps = 0, onStep }) {
    const usesProxy = playUrl !== rawUrl;
    const out = { usesProxy, server: null, device: null, needKbps: 0 };
    if (usesProxy) {
        if (onStep) onStep('server');
        try {
            out.server = await account.request('GET', '/speedtest?url=' + encodeURIComponent(rawUrl), { timeout: TEST_MS + 25000 });
            if (out.server.error) out.server = { error: out.server.error, kbps: 0, size: out.server.size || 0 };
        } catch (e) {
            out.server = { error: e.message || 'network', kbps: 0, size: 0 };
        }
    }
    if (onStep) onStep('device');
    try {
        out.device = await measureDownload(playUrl);
    } catch (e) {
        out.device = { error: e.message || 'network', kbps: 0, size: 0 };
    }
    const size = (out.device && out.device.size) || (out.server && out.server.size) || 0;
    if (size && durationSecs) out.needKbps = Math.round((size * 8) / durationSecs / 1000);
    else if (bitrateKbps) out.needKbps = Math.round(bitrateKbps);
    return out;
}
